import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Save, Trash2, Pencil, X, Store as StoreIcon, Archive } from "lucide-react";
import { listStores, createStore, updateStore, deleteStore } from "../../../lib/emailTemplates";
import { ClosingHoursEditor, ClosingHoursSummary } from "./ClosingHours";

const EMPTY = {
  name: "", address_line1: "", city: "", state: "", zip_code: "",
  phone: "", email: "", manager_name: "", closing_hours: Array(7).fill(null),
};

const StoresTab = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editDraft, setEditDraft] = useState(EMPTY);

  const reload = async () => {
    setLoading(true);
    try { setStores(await listStores()); }
    catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const handleCreate = async () => {
    if (!draft.name.trim()) { toast.error("Store name is required"); return; }
    setBusy(true);
    try {
      await createStore(draft);
      toast.success("Store added");
      setDraft(EMPTY);
      setAddOpen(false);
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    finally { setBusy(false); }
  };

  const beginEdit = (s) => { setEditId(s.id); setEditDraft({ ...EMPTY, ...s, closing_hours: s.closing_hours || EMPTY.closing_hours }); };
  const cancelEdit = () => { setEditId(null); setEditDraft(EMPTY); };

  const handleSave = async () => {
    if (!editDraft.name.trim()) { toast.error("Store name is required"); return; }
    setBusy(true);
    try {
      await updateStore(editId, editDraft);
      toast.success("Saved");
      cancelEdit();
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const toggleArchive = async (s) => {
    try { await updateStore(s.id, { archived: !s.archived }); await reload(); }
    catch (e) { toast.error("Could not toggle archive"); }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Delete "${s.name}"? This cannot be undone. Prefer Archive if templates still reference it.`)) return;
    try { await deleteStore(s.id); toast.success("Deleted"); await reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-stores-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold flex items-center gap-2">
            <StoreIcon size={20} /> Store Profiles
          </h2>
          <p className="text-xs text-[color:var(--ups-subtext)] mt-1">
            {stores.length} store{stores.length === 1 ? "" : "s"} — used to auto-fill email templates.
          </p>
        </div>
        <button
          onClick={() => setAddOpen(true)}
          className="btn-brutal px-3 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1.5"
          data-testid="add-store-btn"
        >
          <Plus size={14} /> Add store
        </button>
      </div>

      {addOpen && (
        <StoreForm
          title="New store"
          draft={draft}
          setDraft={setDraft}
          busy={busy}
          onSubmit={handleCreate}
          onCancel={() => { setAddOpen(false); setDraft(EMPTY); }}
        />
      )}

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="animate-spin" /></div>
      ) : stores.length === 0 ? (
        <div
          className="p-10 text-center rounded-sm text-[color:var(--ups-subtext)]"
          style={{ background: "#fff", border: "1px dashed var(--ups-border)" }}
        >
          No stores yet — add your first one so templates have a signature to render with.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {stores.map((s) => (
            <div
              key={s.id}
              className="p-4 rounded-sm"
              style={{
                background: s.archived ? "#F5F5F4" : "#fff",
                border: "1px solid var(--ups-border)",
                opacity: s.archived ? 0.6 : 1,
              }}
              data-testid={`store-card-${s.id}`}
            >
              {editId === s.id ? (
                <StoreForm
                  title={`Editing: ${s.name}`}
                  draft={editDraft}
                  setDraft={setEditDraft}
                  busy={busy}
                  onSubmit={handleSave}
                  onCancel={cancelEdit}
                />
              ) : (
                <>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-display text-[18px] font-bold">{s.name}</div>
                      {s.manager_name && (
                        <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--ups-subtext)] mt-0.5">
                          {s.manager_name}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => beginEdit(s)} className="p-1.5 rounded-sm hover:bg-[color:var(--ups-muted)]" title="Edit" data-testid={`store-edit-${s.id}`}>
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => toggleArchive(s)} className="p-1.5 rounded-sm hover:bg-[color:var(--ups-muted)]" title={s.archived ? "Unarchive" : "Archive"}>
                        <Archive size={14} />
                      </button>
                      <button onClick={() => handleDelete(s)} className="p-1.5 rounded-sm hover:bg-red-50 text-red-700" title="Delete">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <dl className="mt-3 space-y-1 text-[12px] font-mono leading-snug">
                    {s.address_line1 && <div>{s.address_line1}</div>}
                    {(s.city || s.state || s.zip_code) && (
                      <div>{[s.city, [s.state, s.zip_code].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</div>
                    )}
                    {s.phone && <div>{s.phone}</div>}
                    {s.email && <div>{s.email}</div>}
                  </dl>
                  <ClosingHoursSummary hours={s.closing_hours} />
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const StoreForm = ({ title, draft, setDraft, busy, onSubmit, onCancel }) => {
  const F = (k, label, placeholder = "", colSpan = 1) => (
    <label className={`block ${colSpan === 2 ? "sm:col-span-2" : ""}`}>
      <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">{label}</span>
      <input
        type="text"
        value={draft[k] || ""}
        onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
        placeholder={placeholder}
        className="input-swiss w-full px-2 py-1.5 mt-0.5 rounded-sm text-[13px]"
        data-testid={`store-field-${k}`}
      />
    </label>
  );
  return (
    <div className="p-4 rounded-sm" style={{ background: "#FFF8E1", border: "2px solid #FFB500" }}>
      <div className="font-display text-[14px] font-bold mb-3">{title}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {F("name", "Store name *", "The UPS Store #3423", 2)}
        {F("address_line1", "Street address", "123 Main St", 2)}
        {F("city", "City", "Roswell")}
        <div className="grid grid-cols-2 gap-3">
          {F("state", "State", "GA")}
          {F("zip_code", "ZIP", "30075")}
        </div>
        {F("phone", "Phone", "(770) 555-1234")}
        {F("email", "Email", "store3423@theupsstore.com")}
        {F("manager_name", "Manager name", "Alex Johnson", 2)}
        <ClosingHoursEditor hours={draft.closing_hours} onChange={(v) => setDraft({ ...draft, closing_hours: v })} />
      </div>
      <div className="flex items-center justify-end gap-2 mt-4">
        <button onClick={onCancel} className="px-3 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-white" style={{ border: "1px solid var(--ups-border)" }}>
          Cancel
        </button>
        <button onClick={onSubmit} disabled={busy} className="btn-brutal px-4 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1.5 disabled:opacity-50" data-testid="store-save-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save
        </button>
      </div>
    </div>
  );
};

export default StoresTab;
